/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import _ from 'lodash';
import isEqual from 'lodash.isequal';
import isObject from 'lodash.isobject';
import {
  Device,
  DeviceServiceTypes,
  EntityStatus,
  GenericObject,
  LinuxDeviceFile,
  linuxDeviceFileStatus
} from '@ion/core/src/types/entities';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import {PLATFORM_IDS} from '@ion/core/src/utils/DeviceUtils';
import {safeJSONParse} from '@ion/core/src/utils/JSONUtils';
import {getDeviceServices} from '../../helpers';
import {
  cancelledStatus,
  completedStatus,
  failedStatus,
  wipStatus
} from './FileHistoryStatusCell';

export enum listEventObj {
  jbusDtc1939Event = 'jbusdtc1939',
  jbusDtc1708Event = 'jbusdtc1708',
  vehicleBusCapabilitiesEvent = 'VehicleBusCapabilities',
  appMessage = 'app',
  motionLogMessageEvent = 'motionlog',
  selfDescribingJPODMessage = 'jpodselfdescribing',
  deviceCommandEvent = 'devicecommand',
  idReportEvent = 'idreport',
  extendedIdReportEvent = 'extendedIdreport',
  userMessage = 'user',
  aempMessageEvent = 'aemp',
  jpod2DTCReport = 'dtc2Report'
};

export enum listOfMsgType {
  AvlEvents = 'AVL',
  DeviceCommandEvents = 'DEVICE_COMMAND',
  DtcEvents = 'DTC',
  JbusConstructionDailyReportEvents = 'JBUS_CONSTRUCTION_DAILY_REPORT',
  JbusConstructionDailyUsageReportEvents = 'JBUS_CONSTRUCTION_DAILY_USAGE_REPORT',
  JbusConstructionHourlyReportEvents = 'JBUS_CONSTRUCTION_HOURLY_REPORT',
  JbusDailyReportEvents = 'JBUS_DAILY_REPORT',
  JbusDtc1708Events = 'JBUS_DTC1708',
  JbusDtc1939Events = 'JBUS_DTC1939',
  JbusEvents = 'JBUS',
  JbusFaultReportEvents = 'JBUS_FAULT_REPORT',
  JbusDiscoveryReportEvents = 'JBUS_DISCOVERY_REPORT',
  JbusHourlyReportEvents = 'JBUS_HOURLY_REPORT',
  JbusHydraulicReportEvents = 'JBUS_HYDRAULIC_REPORT',
  IdReportEvents = 'ID_REPORT',
  VehicleBusCapabilitiesEvents = 'VEHICLE_BUS_CAPABILITIES',
  ExtendedIdReportEvents = 'EXTENDED_ID_REPORT',
  UserMessages = 'USER',
  AempMessageEvents = 'AEMP',
  AppMessages = 'APP',
  MotionLogMessageEvents = 'MOTION_LOGS',
  SelfDescribingJPODMessages = 'SELF_DESCRIBING_JPOD',
  LocoEvents = 'LOCO_MESSAGE',
  TagReports = 'TAG_REPORT',
  TrackerEvents = 'TRACKER',
  LmuGatewayEvents = 'LMU_GATEWAY',
  Jpod2DTCReports = 'DTC_REPORT',
  VideoEvents = 'VIDEO_EVENT'
};

export enum triggerExpressions {
  lt = '<',
  gt = '>',
  eq = '==',
  ne = '!=',
  le = '<=',
  ge = '>=',
  in = 'in'
};

export enum vbusStatusText {
  on = 'ON',
  off = 'OFF',
  pending = 'Pending Re-Enable'
};

export enum platformIDText {
  fileType = 'File Type'
};

export enum FileTypesFields {
  ANDROIDMDT = 'Android MDT',
  LINUXOPKG = 'Linux OPKG',
  LMUCONFIG = 'LMU Config'
};

export enum JPOD2Type {
  JPOD2CONFIG = 'JPOD2 Config',
  JPOD2FIRMWARE = 'JPOD2 Firmware'
};

export enum JobStatusType {
  COMPLETED = 'Completed',
  IN_PROGRESS = 'InProgress',
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS'
}

export const adminDevicesViewEvents ='admin-devices:view.events';

export const getDeviceIdentifier = (device) => {
  return device?.esn ||
    device?.serialNumber ||
    device?.iccid ||
    device?.meid ||
    device?.imei ||
    device?.imei2 ||
    device?.imsi ||
    device?.mdn ||
    device?.externalId ||
    device?.ipAddress ||
    device?.id ||
    device?.deviceId;
};

export const renderIcnbleivtData = (device, userApplications) => {
  const {icnEnabled, bleEnabled, ivtEnabled} = device;
  const additionalServices = getDeviceServices(userApplications);
  const {icn, ble, iOn} = additionalServices;
  const icnService = icn ? icnEnabled === EntityStatus.Enabled ? DeviceServiceTypes.icn : icnEnabled === DeviceServiceTypes.PendingEnable ? `${DeviceServiceTypes.icn}*` : '' : '';
  const bleService = ble ? bleEnabled === EntityStatus.Enabled ? DeviceServiceTypes.tagDetection : bleEnabled === DeviceServiceTypes.PendingEnable ? `${DeviceServiceTypes.tagDetection}*` : '' : '';
  const ivtService = iOn ? ivtEnabled === EntityStatus.Enabled ? DeviceServiceTypes.videoService : ivtEnabled === DeviceServiceTypes.PendingEnable ? `${DeviceServiceTypes.videoService}*` : '' : '';
  return icnService + (icnService !== '' && bleService !== '' ? ', ' + bleService : bleService) + (((bleService !== '' || icnService !== '') && ivtService !== '') ? ',' + ivtService : ivtService);
};

export const formatEnabledServices = (device: Device): string => {
  const {features} = device;
  if (features) {
    return Object.entries(features)
      ?.filter(([, status]) => status === EntityStatus.Enabled || status === DeviceServiceTypes.PendingEnable)
      ?.map(([label, status]) => (
        `${label}${status === DeviceServiceTypes.PendingEnable ? '*' : ''}`
      ))
      .sort()
      .join(', ');
  }
  return '';
};

export const getFirstIdReport = (device) => {
  if (device?.idReport?.statusCode) {
    return null;
  }
  return formatDateTime(device?.idReport?.firstIdReportTime);
};

export const getAVLAddress = (device) => {
  const address = device?.lastAvlCommunication?.address;
  if (address !== undefined) {
    return `${address?.street}, ${address?.city}, ${address?.stateProvinceCd} ${address?.zip} ${address?.country}`;
  }
  return '';
};

export const getMDTJobId = (file) => {
  const {
    command,
    commandResult,
    jobStatus,
    jobId,
    currentLength,
    transactionStatus
  } = file || {};
  const {COMPLETED, IN_PROGRESS, SUCCESS} = JobStatusType || {};
  if ((jobStatus === COMPLETED) || (jobStatus === IN_PROGRESS && transactionStatus === SUCCESS)) {
    if (command?.job) {
      return command?.job?.jobId;
    } else if (commandResult?.properties?.job) {
      return commandResult?.properties?.job?.jobId;
    } else if (currentLength > 0) {
      return jobId;
    }
  }
};

export const getMDTCurrentVersion = (file) => {
  if (file?.currentLength > 0 ||
    file?.commandResult?.status !== JobStatusType.PENDING ||
    file?.transactionStatus !== JobStatusType.PENDING) {
    return file?.newVersion;
  }
};

export const getMDTPendingJobId = (file) => {
  if (file?.pendingLength > 0) {
    return file?.jobId;
  }
};

export const getMDTPendingJobStatus = (file) => {
  if (file?.pendingLength > 0 || file?.currentLength > 0) {
    return file?.jobStatus ? file?.transactionStatus : '';
  }
};

export const getMDTPendingFileVersion = (file) => {
  if (file?.pendingLength > 0) {
    return file?.newVersion;
  }
};

export const getPendingJobStatus = (file) => {
  const {pending, current} = file || {};
  return (pending?.[0]?.jobId && pending?.[0]?.transactionStatus) ||
          (current?.[0]?.jobId && current?.[0]?.transactionStatus);
};

export const getPlatFormValues = (file) => {
  const platforms         = file?.newDeviceFile?.platforms;
  const platformsFileType = platforms?.filter(fileType => !fileType?.description?.includes('App ID'))?.map(type => type?.value);
  const platform          = platforms?.find(platform => platform?.platformType?.name === 'file_types');
  const JPOD2Items        = platformsFileType.filter(item => (item === JPOD2Type.JPOD2CONFIG ||
    item === JPOD2Type.JPOD2FIRMWARE));
  if (JPOD2Items.length === 0) {
    return platformsFileType;
  }

  return platform?.value || JPOD2Items || '';
};

export const getPlatform = (file) => {
  return file?.platforms ? file?.platforms?.value : getPlatFormValues(file);
};

export const getJobId = (file) => {
  let jobId = '';
  if (file?.command?.job) {
    jobId = file?.command?.job?.jobId;
  } else if (file?.commandResult?.properties?.job) {
    jobId = file?.commandResult?.properties?.job?.jobId;
  } else if (file?.current) {
    file?.current.forEach(jobIdVal => {
      jobId = jobIdVal?.jobId;
    });
  }
  return jobId;
};

export const getCurrentLastUpdateTime = (file) => {
  let lastUpdateTime = '';
  if (file?.current) {
    file?.current.forEach(lastUpdateTimeVal => {
      lastUpdateTime = lastUpdateTimeVal?.lastUpdateTime;
    });
  }
  return formatDateTime(lastUpdateTime);
};

export const getCurrentNewVersion = (file) => {
  let currentFileVersion = '';
  if (file?.current) {
    file?.current.forEach(currentFileVersionVal => {
      currentFileVersion = currentFileVersionVal?.newVersion;
    });
  }
  return currentFileVersion;
};

export const getVersion = (file: File, useNewVersion = true): string => {
  const item = file[useNewVersion ? 'oldDeviceFile' : 'newDeviceFile'];
  if (item) {
    if (item.version && item.reportedName) {
      return `${item.version} ${'('} ${item.reportedName} ${')'}`;
    }
    return item.version;
  }
  return '';
};

export const getPlatFormIdLists = (fileTypes) => {
  const platformIdLists = [];
  if (fileTypes?.length) {
    fileTypes?.forEach((fileType) => {
      platformIdLists.push(parseInt(fileType.platformId, 10));
    });
  }
  return platformIdLists;
};

export const getPlatformId = (platforms) => {
  if (platforms?.length) {
    const [first, second] = platforms;
    if (first?.description?.includes(platformIDText.fileType) || !second) {
      return first?.id;
    } else {
      return second?.id;
    }
  }
  if (Object.keys(platforms).length) {
    if (platforms || platforms?.description?.includes(platformIDText.fileType)) {
      return platforms?.id;
    }
  }
  return undefined;
};

export const filterByplatformId = (data, selctedPlatformId) => {
  const platformId = parseInt(selctedPlatformId, 10);
  const result = [];
  _.filter(data, (row) => {
    const platforms = row?.platforms || row?.newDeviceFile?.platforms;
    const mappedPlatformId = getPlatformId(platforms);
    if (platforms && platformId === mappedPlatformId) {
      result.push(row);
    }
  });
  return result;
};

export const filterByAllFileTypes = (data, platformIdLists) => {
  const result = [];
  _.filter(data, (row) => {
    const platforms = row?.platforms || row?.newDeviceFile?.platforms;
    const mappedPlatformId = getPlatformId(platforms);
    if (platforms && platformIdLists.indexOf(mappedPlatformId) > -1) {
      result.push(row);
    }
  });
  return result;
};

export const filterFilesByStatus = (data: GenericObject, statusObj: GenericObject) => {
  const {failed, cancelled, completed, wip} = statusObj;
  const cancelledSearch = cancelled ? [cancelled] : Object.keys(cancelledStatus);
  const completedSearch = completed ? [completed] : Object.keys(completedStatus);
  const failedSearch = failed ? [failed] : Object.keys(failedStatus);
  const wipSearch = wip ? [wip] : Object.keys(wipStatus);
  const searchOptions = [...cancelledSearch, ...completedSearch, ...failedSearch, ...wipSearch];
  return data?.filter((item) => {
    const status = item?.transactionStatus?.name;
    return searchOptions.includes(status);
  });
};

export const getFileHistoryByStatus = (data: GenericObject, status: string) => {
  return data?.filter((item) => status === item?.transactionStatus?.name);
};

export const parse = (files: LinuxDeviceFile) => {
  const report  = files?.rawIdReport;
  const items   = report?.split('|');
  items?.shift();
  const results = items.filter(item => item).map(item => {
    const row    = item.split(',');
    const status = linuxDeviceFileStatus[row[5]] || '';
    const [index, fileType, fileName, fileHash, Version] = row;
    return {
      Status : status,
      fileType,
      fileName,
      fileHash,
      Version,
      index
    };
  });
  return results;
};

export const getFirmwareVersion = ({idReport, extensions}: Device, displayFirmwareOnly = false): string => {
  const {device}                 = idReport || {};
  const {firmwareVersion}        = extensions || {};
  const _appId: string           = device?.idReport?.appId || null;
  const _firmwareVersion: string = device?.idReport?.firmwareVersion || null;
  if (displayFirmwareOnly) {
    return firmwareVersion || null;
  }
  if (firmwareVersion) {
    return firmwareVersion;
  }
  if (_appId && _firmwareVersion) {
    return `${_firmwareVersion}.${_appId}`;
  }
  return _firmwareVersion || _appId;
};

export const getConfigVersion = ({
  idReport,
  extensions
}: Device,
includeScriptVersion = false,
isCtcAdmin = false): string => {
  const {device}               = idReport || {};
  const {configVersion}        = extensions || {};
  const {scriptVersion}        = extensions || {};
  const _scriptVersion: string = device?.idReport?.scriptVersion || null;
  const _configVersion: string = device?.idReport?.configVersion || null;
  if (isCtcAdmin && configVersion) {
    return configVersion;
  }
  if (configVersion && !includeScriptVersion) {
    return configVersion;
  }
  if (_scriptVersion && _configVersion && !includeScriptVersion) {
    return `${_scriptVersion}.${_configVersion}`;
  }
  if (includeScriptVersion) {
    if (configVersion && scriptVersion) {
      return `${scriptVersion}.${configVersion}`;
    } else if (configVersion) {
      return `${configVersion}`;
    } else if (scriptVersion) {
      return `${scriptVersion}`;
    }
  } else {
    return _scriptVersion || _configVersion;
  }
};

export const getMDN = ({mdn, min}: Device) => {
  return min || mdn || null;
};

export const getVbusStatus = (device: Device): string => {
  const deviceStatus = device?.extensions?.vbusEnabledStatus;
  const status       = _.isString(deviceStatus) ? safeJSONParse(deviceStatus) : deviceStatus;
  if (_.isUndefined(status)) return '';
  if (status) {
    return vbusStatusText.on;
  }
  return vbusStatusText.off;
};

/**
 * Defines a utility to convert object/nested object to array and return
 * the appropriate string for the MFG device.
 *
 * ```typescript
 * import {getMFGInfoObject} from '../ViewDeviceEditorUtils';
 *
 * return getMFGInfoObject(manufacturingInfo);
 *
 * ```
 *
 * @param manufacturingInfo The value that will be used to display the string(s).
 * @returns {string} The string(s) for the MFG Device.
 */
const getMFGInfoObject = (manufacturingInfo: GenericObject): string => {
  return Object.entries(manufacturingInfo).map(([key, value]) => {
    if (isObject(value)) {
      return `${key} : ${getMFGInfoObject(value)}`;
    } else {
      return `${key} : ${value}`;
    }
  }).join('\n');
};

/**
 * Defines a utility to return boolean based on manufacturingInfo params.
 *
 * ```typescript
 * import {isMFGInfoArray} from '../ViewDeviceEditorUtils';
 *
 * isMFGInfoArray(manufacturingInfo);
 *
 * ```
 *
 * @param manufacturingInfo The value that will be used to return the boolean.
 * @returns {boolean} True if manufacturingInfo is an array, otherwise, false.
 */
const isMFGInfoArray = (manufacturingInfo: GenericObject): boolean => {
  return Array.isArray(manufacturingInfo);
};

/**
 * Defines a utility to return the appropriate string for the MFG device.
 *
 * ```typescript
 * import {getMFGData} from '../ViewDeviceEditorUtils';
 *
 * getMFGData(rawData, mfgInfo);
 *
 * ```
 *
 * @param rawData The value that will be used to display the string(s).
 * @param mfgInfo The value that will be used to display the string(s).
 * @returns {string} The string(s) for the MFG Device.
 */
export const getMFGData = (rawData, mfgInfo): string => {
  if (isObject(rawData)) {
    return getMFGInfoObject(rawData);
  }
  return rawData || mfgInfo;
};

/**
 * Defines a utility to return the appropriate string for the MFG device.
 *
 * ```typescript
 * import {getMFGInfo} from '../ViewDeviceEditorUtils';
 *
 * const mfgInfo = getMFGInfo(manufacturingInfo);
 *
 * ```
 *
 * @param manufacturingInfo The value that will be used to display the string(s).
 * @returns {string} The string(s) for the MFG Device.
 */
export const getMFGInfo = (manufacturingInfo): string => {
  if (!isMFGInfoArray(manufacturingInfo)) {
    return getMFGInfoObject(manufacturingInfo);
  } if (isObject(manufacturingInfo?.[0])) {
    return getMFGInfoObject(manufacturingInfo?.[0]);
  }
  const firstInfo = manufacturingInfo?.[0];
  if (firstInfo && isObject(firstInfo)) {
    return getMFGInfoObject(firstInfo);
  }
  return manufacturingInfo;
};

export const getFileStatus = (device: Device, fileType: boolean = false): string => {
  const fileHistory = device?.fileHistoryPending;
  if (fileHistory?.length) {
    return fileHistory?.filter(({platformId}) => {
      const isConfig = isEqual(platformId, PLATFORM_IDS.config) || isEqual(platformId, PLATFORM_IDS.peg2);
      const isFirmware = isEqual(platformId, PLATFORM_IDS.firmware);
      return fileType ? isConfig : isFirmware;
    })?.map(history => history?.transactionStatus || '');
  } else {
    return '';
  }
};
